// SOAP 生成モデルの「version」軸 (= v1 / v2 / v3) を扱う helper.
//
// back は soap_prompt_version を完全な semver 文字列 (例: '1.0.0' / '2.1.0') で保持するが、
// ユーザー手元には major (v1 / v2 / v3) だけを見せ、選択時は当該 major の最新 patch を自動設定する。
// 「prompt version」 という内部表現はUIに露出させない。

export type SoapModelMajor = 'v1' | 'v2' | 'v3'

const SOAP_MODEL_MAJORS = ['v1', 'v2', 'v3'] as const

const majorNum = (version: string): number =>
  parseInt(version.split('.')[0] ?? '', 10)

const compareSemver = (a: string, b: string): number => {
  const pa = a.split('.').map((n) => parseInt(n, 10) || 0)
  const pb = b.split('.').map((n) => parseInt(n, 10) || 0)
  for (let i = 0; i < 3; i++) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0)
    if (diff !== 0) return diff
  }
  return 0
}

// available 文字列配列から各 major (1.x / 2.x / 3.x) の最新版を解決.
//   例: ['1.0.0', '2.0.0', '2.1.0'] → { v1: '1.0.0', v2: '2.1.0' }
export const resolveSoapModelLatest = (
  available: string[] | undefined
): Partial<Record<SoapModelMajor, string>> => {
  const result: Partial<Record<SoapModelMajor, string>> = {}
  for (const major of [1, 2, 3] as const) {
    const inMajor = (available ?? [])
      .filter((v) => majorNum(v) === major)
      .sort(compareSemver)
    if (inMajor.length > 0) {
      result[`v${major}` as SoapModelMajor] = inMajor[inMajor.length - 1]
    }
  }
  return result
}

// 現在の version 文字列が属する major. 未該当は ''.
export const soapModelMajorOf = (
  version: string | undefined
): SoapModelMajor | '' => {
  const n = majorNum(version ?? '')
  if (n === 1) return 'v1'
  if (n === 2) return 'v2'
  if (n === 3) return 'v3'
  return ''
}

// v3 (記載傾向の蒸留・SoapRule の新世界) かどうか。
// nav / 設定画面の version 連動表示はこの1点で分岐する。
export const isV3SoapModel = (version: string | undefined): boolean =>
  majorNum(version ?? '') >= 3

// 系統セレクトに出す major の一覧。
// v3 は GA (2026-08)。release_channel による出し分けは無く、配信されている major を
// そのまま出す — どの店舗も自力で v2⇄v3 を往復できる状態が既定。
// UI 露出制御はこの1点のみ (server 側では制御しない)。
export const selectableSoapModelMajors = (
  available: string[] | undefined
): SoapModelMajor[] => {
  const latest = resolveSoapModelLatest(available)
  return SOAP_MODEL_MAJORS.filter((m) => latest[m])
}
